import { Box, Switch } from '@mui/material';
import { ReactElement, useMemo } from 'react';
import { tokens } from '@synergy/theme-palo';
import { useSearchParams } from 'react-router-dom';

/** The switch in the filter row, written into the address so it is kept. */
export const PALO_INCLUDE_LOCKED = 'includeLocked';

/** The field a list is narrowed by when locked records are asked for by name. */
const LOCKED_FIELD = 'isLocked';

/**
 * Whether a master data list shows locked records, and what it asks for.
 *
 * A locked partner is one nobody may work with any more, and in the lists they
 * made up to a sixth of the rows — in the person list 1.660 of 10.118. They
 * are hidden unless asked for (LST-S14, GF 09.10.2026), which is a system
 * default and not a filter the user set: it holds whenever nothing else is
 * said, and the switch that lifts it is kept like any other filter (LST-S15).
 *
 * Asking for the locked ones by name — "gesperrt: ja" from the filter panel —
 * outranks both: then the list shows exactly what was asked for.
 */
export const usePaloLockedRecords = (
    params: URLSearchParams
): {
    /** Whether locked records stand in the list. */
    includeLocked: boolean;
    setIncludeLocked: (on: boolean) => void;
    /** The parameters the list asks the server with. */
    params: URLSearchParams;
} => {
    const [searchParams, setSearchParams] = useSearchParams();
    const asked = searchParams.get(LOCKED_FIELD);
    const includeLocked = '1' === searchParams.get(PALO_INCLUDE_LOCKED) || null !== asked;
    const query = params.toString();

    const request = useMemo(() => {
        const next = new URLSearchParams(query);

        // The switch is the list's own business; the server is told what it
        // means instead.
        next.delete(PALO_INCLUDE_LOCKED);

        if (!includeLocked) {
            next.set(LOCKED_FIELD, 'false');
        }

        return next;
    }, [query, includeLocked]);

    const setIncludeLocked = (on: boolean): void => {
        const next = new URLSearchParams(searchParams);

        if (on) {
            next.set(PALO_INCLUDE_LOCKED, '1');
        } else {
            next.delete(PALO_INCLUDE_LOCKED);
            // A list asked for the locked ones by name cannot be put back by
            // this switch alone; the named filter goes with it.
            next.delete(LOCKED_FIELD);
        }

        next.set('page', '1');
        setSearchParams(next);
    };

    return { includeLocked, setIncludeLocked, params: request };
};

/** What the empty state offers when a search only matches locked records. */
export const paloLockedHint = (count: number): string =>
    `${count} ${1 === count ? 'gesperrten Treffer' : 'gesperrte Treffer'} anzeigen`;

/** The switch in the filter row that shows the locked records as well. */
export const PaloLockedSwitch = ({
    checked,
    onChange,
    label = 'Gesperrte anzeigen',
}: {
    checked: boolean;
    onChange: (on: boolean) => void;
    label?: string;
}): ReactElement => (
    <Box
        component="label"
        sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            height: 32,
            fontSize: '12.5px',
            color: tokens.color.ink2,
            cursor: 'pointer',
            userSelect: 'none',
            whiteSpace: 'nowrap',
        }}
    >
        <Switch size="small" checked={checked} onChange={event => onChange(event.target.checked)} />
        {label}
    </Box>
);
